/**
 * Generic eCRF pack provisioner.
 *
 * Extracted from OptimiseTBTemplateLoader so any pack in
 * public.ecrf_template_packs can be installed at a trial site
 * by the new Install Trial Wizard.
 *
 * Idempotent: skips forms already present at the site by name.
 * Returns the full list of in-site form rows (existing + newly inserted)
 * so callers can wire visit_form_bundles.
 */

import { supabase } from '@/integrations/supabase/client';

interface PackField {
  key: string;
  type: string;
  options?: string[];
  min?: number;
  max?: number;
  formula?: string;
}
interface PackFormDef {
  code: string;
  label: string;
  fields: PackField[];
  visits?: string[]; // optional: which visit codes this form applies to
}

export interface ProvisionedForm {
  id: string;
  form_name: string;
  pack_form_code: string;
  applies_to_visits: string[] | null;
}

function mapField(f: PackField) {
  let type: string;
  switch (f.type) {
    case 'select': type = 'select'; break;
    case 'number': type = 'number'; break;
    case 'date': type = 'date'; break;
    case 'boolean': type = 'radio'; break;
    case 'computed': type = 'text'; break;
    default: type = 'text';
  }
  const out: Record<string, unknown> = {
    id: f.key,
    type,
    label: f.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    required: f.type !== 'computed',
  };
  if (f.options) out.options = f.options.map((v) => ({ value: v, label: v }));
  if (f.type === 'boolean') {
    out.options = [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ];
  }
  if (typeof f.min === 'number') out.min = f.min;
  if (typeof f.max === 'number') out.max = f.max;
  if (f.formula) out.formula = f.formula;
  return out;
}

export async function provisionECRFPack(opts: {
  siteId: string;
  packCode: string;
  formNamePrefix?: string;
}): Promise<{ pack: { code: string; name: string; version: string } | null; forms: ProvisionedForm[]; insertedCount: number }> {
  const { siteId, packCode } = opts;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = supabase as any;
  const { data: pack, error: packErr } = await client
    .from('ecrf_template_packs')
    .select('pack_code, pack_name, version, form_definitions, visit_schedule, protocol_alignment, therapeutic_area')
    .eq('pack_code', packCode)
    .eq('is_active', true)
    .maybeSingle();
  if (packErr) throw packErr;
  if (!pack) return { pack: null, forms: [], insertedCount: 0 };

  const prefix = opts.formNamePrefix ?? `${pack.pack_name}: `;
  const defs = (pack.form_definitions ?? []) as PackFormDef[];

  const targets = defs.map((def) => ({
    form_name: `${prefix}${def.label}`,
    pack_form_code: def.code,
    applies_to_visits: def.visits ?? null,
    form_type: 'crf' as const,
    version: pack.version as string,
    form_schema: {
      formTitle: def.label,
      formDescription: `${pack.pack_name} · ${pack.protocol_alignment ?? ''}`.trim(),
      fields: def.fields.map(mapField),
      meta: {
        pack_code: pack.pack_code,
        form_code: def.code,
        visit_schedule: pack.visit_schedule,
        applies_to_visits: def.visits ?? null,
        therapeutic_area: pack.therapeutic_area,
      },
    },
  }));

  // Existing rows for idempotency
  const { data: existing, error: existErr } = await supabase
    .from('trial_protocol_forms')
    .select('id, form_name')
    .eq('trial_site_id', siteId)
    .in('form_name', targets.map((t) => t.form_name));
  if (existErr) throw existErr;
  const existingByName = new Map((existing ?? []).map((r) => [r.form_name, r.id]));

  const toInsert = targets.filter((t) => !existingByName.has(t.form_name)).map((t) => ({
    trial_site_id: siteId,
    form_name: t.form_name,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form_type: t.form_type as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form_schema: t.form_schema as any,
    version: t.version,
    is_active: true,
  }));

  let insertedRows: { id: string; form_name: string }[] = [];
  if (toInsert.length > 0) {
    const { data, error } = await supabase
      .from('trial_protocol_forms')
      .insert(toInsert)
      .select('id, form_name');
    if (error) throw error;
    insertedRows = data ?? [];
  }

  const allByName = new Map<string, string>([
    ...existingByName,
    ...insertedRows.map((r): [string, string] => [r.form_name, r.id]),
  ]);

  const forms: ProvisionedForm[] = targets.map((t) => ({
    id: allByName.get(t.form_name) as string,
    form_name: t.form_name,
    pack_form_code: t.pack_form_code,
    applies_to_visits: t.applies_to_visits,
  }));

  return {
    pack: { code: pack.pack_code, name: pack.pack_name, version: pack.version },
    forms,
    insertedCount: insertedRows.length,
  };
}
